import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { supabase } from './supabase';
import { authManager } from './authManager';
 
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function truncateDescription(description: string | undefined) {
  if (!description) return '';
  const plainText = description.replace(/<[^>]*>/g, '');
  return plainText.length > 30 ? plainText.substring(0, 30) + '...' : plainText;
}

export function formatCurrency(value: number, options: Intl.NumberFormatOptions = {}) {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  }).format(value);
}

export function formatDate(date: string | Date | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) return '';

    return new Intl.DateTimeFormat('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...options
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

// A single source of truth for getting user and organization data
// This helps reduce redundant API calls throughout the application
export const getUserData = async () => {
  try {
    // Try to get from SessionStorage first if available
    if (typeof window !== 'undefined') {
      const cachedUserData = sessionStorage.getItem('userData');
      if (cachedUserData) {
        const parsedData = JSON.parse(cachedUserData);
        const now = Date.now();
        
        // Check if cached data is still valid (less than 5 minutes old)
        if (parsedData.timestamp && now - parsedData.timestamp < 5 * 60 * 1000) {
          return parsedData.data;
        }
      }
    }

    // If not in session storage or expired, fetch from API
    const user = await authManager.getUser();
    if (!user) throw new Error('User not authenticated');

    const userId = user.id;

    const { data: userProfile, error: profileError } = await supabase
      .from('user_profiles')
      .select('*, organization_id, is_super_admin')
      .eq('id', userId)
      .single();

    if (profileError) throw profileError;
    if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

    const result = {
      userId,
      organizationId: userProfile.organization_id,
      isSuperAdmin: userProfile.is_super_admin || false,
      userProfile
    };

    // Cache in sessionStorage if available
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('userData', JSON.stringify({
        data: result,
        timestamp: Date.now()
      }));
    }

    return result;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

// Clear all user-related caches (used on logout)
export const clearUserDataCache = () => {
  if (typeof window !== 'undefined') {
    // Rimuovi cache utente
    sessionStorage.removeItem('userData');

    // Pulisci tutte le chiavi di cache WhatsApp in sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      if (key.includes('whatsapp_config_cache')) {
        sessionStorage.removeItem(key);
      }
    });

    // Pulisci cache in localStorage che potrebbero contenere dati dell'utente
    Object.keys(localStorage).forEach(key => {
      // Rimuovi qualsiasi item che potrebbe contenere dati di chat o messaggi
      if (key.includes('whatsapp') || key.includes('chat') || key.includes('message')) {
        localStorage.removeItem(key);
      }
    });
  }
};